// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useBanksConnect } from './use-banks-connect';
import { AppIcon, AssetLogo, classNames } from '@shared/ui-kit';
import { Input, OptionsSelector } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { Alert, Button, Chip, Tooltip, Typography } from '@mui/material';
import { Loader, type LoaderStatus } from '../loader';
import styles from './banks-connect.module.css';

export const BanksConnect = defineComponent({
  displayName: 'BanksConnect',
  useSetup: useBanksConnect,
  View: ({
    t,
    isDrawer,
    countryOptions,
    country,
    search,
    institutions,
    countryByCode,
    isLoading,
    isError,
    isConnecting,
    hasInstitutions,
    canContinue,
    onRetry,
    onCountryChange,
    onSearchChange,
    onSelectInstitution,
    onContinue,
    onBack,
  }) => {
    const loaderStatus: LoaderStatus = isError
      ? 'rejected'
      : isLoading
      ? 'pending'
      : 'fulfilled';

    const body = (
      <>
        {!isDrawer ? (
          <div className={styles.header}>
            <Typography variant="h4" className={styles.title}>
              <Translation i18nKey="aggregator.select-bank" ns="aggregator" />
            </Typography>

            <Typography className={styles.subtitle}>
              <Translation
                i18nKey="aggregator.search-bank-to-connect"
                ns="aggregator"
              />
            </Typography>
          </div>
        ) : null}

        <div className={styles.filters}>
          <div className={styles['country-selector-wrapper']}>
            <OptionsSelector
              optionsAvailable={countryOptions}
              onChangeValueSelected={onCountryChange}
              optionSelected={country}
              defaultText={t('aggregator.all-countries', { ns: 'aggregator' })}
              selectType="white"
              searchBar
              selectedAll={false}
              data-testid="banks-connect-country-selector"
            />
          </div>

          <div className={styles['search-wrapper']}>
            <AppIcon icon="Search" color="disabled" size="base" />
            <Input
              value={search}
              onChange={event => onSearchChange(event.target.value)}
              placeholder={t('aggregator.search-bank-placeholder', {
                ns: 'aggregator',
              })}
              className={styles.search}
            />
          </div>
        </div>

        <Loader
          status={loaderStatus}
          onRefresh={onRetry}
          shouldDisplayContent={!isError}
          errorTitle={
            <Translation i18nKey="aggregator.bank-search-error" ns="aggregator" />
          }
        >
          {!isLoading && !hasInstitutions && (
            <Alert severity="info">
              <Translation i18nKey="aggregator.no-bank-results" ns="aggregator" />
            </Alert>
          )}

          {hasInstitutions && (
            <div
              className={classNames(styles.list, isDrawer && styles['list-drawer'])}
            >
              {institutions.map(institution => (
                <button
                  type="button"
                  key={institution.listKey}
                  onClick={() => onSelectInstitution(institution.listKey)}
                  className={classNames(styles.card, {
                    [styles['card-selected']]: institution.isSelected,
                  })}
                >
                  <div className={styles['card-logo']}>
                    {institution.logo_url ? (
                      <AssetLogo
                        logoUrl={institution.logo_url}
                        className={styles['card-logo-image']}
                      />
                    ) : (
                      <AppIcon icon="Building" color="disabled" size="base" />
                    )}
                  </div>

                  <div className={styles['card-content']}>
                    <div className={styles['card-title-row']}>
                      <Typography className={styles['card-title']}>
                        {institution.name}
                      </Typography>

                      {institution.is_oneshot && (
                        <Tooltip
                          title={
                            <Translation
                              i18nKey="aggregator.one-shot-message"
                              ns="aggregator"
                            />
                          }
                        >
                          <Chip
                            size="small"
                            label={
                              <Translation
                                i18nKey="aggregator.one-shot"
                                ns="aggregator"
                              />
                            }
                            className={styles['one-shot-chip']}
                          />
                        </Tooltip>
                      )}

                      {institution.is_business && (
                        <Chip
                          size="small"
                          label={
                            <Translation
                              i18nKey="aggregator.business"
                              ns="aggregator"
                            />
                          }
                          className={styles['business-chip']}
                        />
                      )}
                    </div>

                    <Typography className={styles['card-country']}>
                      {countryByCode.get(institution.country) ??
                        institution.country}
                    </Typography>
                  </div>

                  <div className={styles['card-action']}>
                    {institution.isSelected && (
                      <AppIcon icon="CheckCircle" color="inherit" size="lg" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Loader>
      </>
    );

    return (
      <div className={classNames(styles.page, isDrawer && styles['page-drawer'])}>
        <div
          className={classNames(
            styles.container,
            isDrawer && styles['container-drawer'],
          )}
        >
          {isDrawer ? <div className={styles['body-drawer']}>{body}</div> : body}

          <div
            className={classNames(
              styles.footer,
              isDrawer && styles['footer-drawer'],
            )}
          >
            <p className={styles['footer-text']}>
              <Translation
                i18nKey="aggregator.one-shot-message-rich"
                ns="aggregator"
                components={[<span className={styles['one-shot-highlight']} />]}
              />
            </p>

            <div
              className={classNames(
                isDrawer ? styles['footer-actions'] : undefined,
              )}
            >
              {isDrawer && onBack ? (
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  size="large"
                  startIcon={<AppIcon icon="ArrowLeft" />}
                  onClick={onBack}
                  data-testid="banks-connect-previous"
                >
                  <Translation i18nKey="common.previous" ns="common" />
                </Button>
              ) : null}

              <Button
                color="secondary"
                variant="contained"
                size="large"
                fullWidth={!isDrawer}
                disabled={!canContinue || isConnecting}
                onClick={() => onContinue()}
                endIcon={isDrawer ? <AppIcon icon="ArrowRight" /> : undefined}
                data-testid="banks-connect-continue"
              >
                <Translation
                  i18nKey="aggregator.connect-selected-bank"
                  ns="aggregator"
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  },
});
